import {
  Box,
  Button,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import { usePrivy } from '@privy-io/react-auth';
import { config } from '../../../shared/constants/WagmiConfig';
import React, { useEffect, useState } from 'react';
import LoadingButton from '../../../components/common/LoadingButton';
import { ethers, BigNumberish } from 'ethers';
import VirtualBalanceChip from '../../../components/common/VirtualBalanceChip';
import { virtualDetailModalStyle } from '../../../components/virtual/VirtualDetailModal.style';
import { enqueueSnackbar } from 'notistack';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { activeChain, checkNetwork } from '../../../shared/utils/CheckNetwork';
import { renderContractError } from '../../../shared/utils/RenderContractError';
import { formatNumber, roundDown } from '../../../shared/utils/Formatter';
import { virtualTokenABI } from '../../../assets/abis/virtualToken';
import { readContract, waitForTransactionReceipt } from 'wagmi/actions';
import { useVirtualBalanceContext } from '../../../context/VirtualBalanceContext';
import TokenInput from '../../../components/common/TokenInput';

interface props {
  onClose: () => void;
}

const ConvertVirtualForm: React.FC<props> = ({ onClose }) => {
  const { user } = usePrivy();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [timelockDuration, setTimelockDuration] = useState<number>(0);
  const [highestLock, setHighestLock] = useState<BigInt>(BigInt(1));
  const [maxTimelock, setMaxTimelock] = useState<number>(0);
  const [minTimelock, setMinTimelock] = useState<number>(0);
  const [mutipleAmount, setMutipleAmount] = useState<bigint>(BigInt(1));
  const [convertAmount, setConvertAmount] = useState<string>('');
  const [canConvert, setCanConvert] = useState<boolean>(false);
  const [convertData, setConvertData] = useState<`0x${string}` | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const { virtualBalance, fetchXVirtualBalance, fetchVirtualBalance } =
    useVirtualBalanceContext();

  const { writeContractAsync } = useWriteContract({
    mutation: {
      onSuccess(data) {
        // On success, wait for tx approved before closing modal
        enqueueSnackbar({
          key: data,
          message: `Transaction is in progress`,
          action: (
            <a
              rel="noreferrer"
              target="_blank"
              href={`${activeChain.blockExplorers.default.url}/tx/${data}`}
            >
              <Button>View Tx</Button>
            </a>
          ),
        });

        setConvertData(data);
      },
      onError(error) {
        const errorMessage = renderContractError(error);
        setErrorMessage(errorMessage);
      },
    },
  });

  const { isSuccess: isTxConfirmed, isLoading: isTxConfirming } =
    useWaitForTransactionReceipt({
      chainId: activeChain.id,
      hash: convertData,
    });

  const convert = async () => {
    if (!convertAmount) return;

    let timelockDurationBigInt = BigInt(
      monthsToSeconds(timelockDuration)
    ) as BigInt;

    let timelock =
      timelockDurationBigInt > highestLock
        ? highestLock
        : timelockDurationBigInt;

    await writeContractAsync({
      chainId: activeChain.id,
      abi: virtualTokenABI,
      address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      functionName: 'deposit',
      args: [
        ethers.parseUnits(convertAmount.toString(), 'ether'),
        timelock as any,
        user?.wallet?.address as `0x${string}`,
      ],
    })
      .then(async (txnHash) => {
        const txReceipt = await waitForTransactionReceipt(config, {
          chainId: activeChain.id,
          hash: txnHash,
        });

        if (txReceipt.status === 'success') {
          enqueueSnackbar({
            message: 'Successfully converted VIRTUAL for xVIRTUAL',
            variant: 'success',
          });
        } else {
          enqueueSnackbar({
            message: 'Transaction failed',
            variant: 'error',
          });
          setIsLoading(false);
        }
        await fetchVirtualBalance();
        await fetchXVirtualBalance();
        onClose();
      })
      .catch(async (e: any) => {
        setIsLoading(false);
      });
  };

  const getTimelock = () => {
    readContract(config, {
      chainId: activeChain.id,
      abi: virtualTokenABI,
      address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      functionName: 'maxLockDuration',
      args: [],
    }).then((response) => {
      let tempMaxTimelock = response as BigInt;
      setHighestLock(tempMaxTimelock as BigInt);
      setMaxTimelock(secondsToMonths(tempMaxTimelock));
    });

    readContract(config, {
      abi: virtualTokenABI,
      address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      functionName: 'MIN_LOCK_DURATION',
      args: [],
    }).then((response) => {
      let tempMinTimelock = response as BigInt;
      setMinTimelock(secondsToMonths(tempMinTimelock));
      retrieveMultiplier(tempMinTimelock as BigInt);
    });
  };

  const retrieveMultiplier = (timelock: BigInt) => {
    setIsFetching(true);
    readContract(config, {
      chainId: activeChain.id,
      abi: virtualTokenABI,
      address: process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      functionName: 'getMultiplier',
      args: [timelock as bigint],
    })
      .then((response) => {
        let tempMutipleAmount = response as any;
        setMutipleAmount(tempMutipleAmount);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const requestAllowance = async (allowanceAmount: bigint) => {
    try {
      const txHash = await writeContractAsync({
        chainId: activeChain.id,
        abi: virtualTokenABI,
        address: process.env.REACT_APP_TOKEN_ADDRESS as `0x${string}`,
        functionName: 'approve',
        args: [
          process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
          allowanceAmount,
        ],
      });

      enqueueSnackbar({
        message: 'Pending allowance confirmation',
        variant: 'info',
      });

      await waitForTransactionReceipt(config, {
        chainId: activeChain.id,
        hash: txHash,
      });

      enqueueSnackbar({ message: 'Allowance approved', variant: 'success' });
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const checkAllowance = async (amount: bigint) => {
    const allowance = await readContract(config, {
      abi: virtualTokenABI,
      address: process.env.REACT_APP_TOKEN_ADDRESS as `0x${string}`,
      functionName: 'allowance',
      args: [
        user?.wallet?.address as `0x${string}`,
        process.env.REACT_APP_X_VIRTUAL_ADDRESS as `0x${string}`,
      ],
    });

    if (!amount) {
      return;
    }

    if (allowance < amount) {
      // Prompt spending allowance
      enqueueSnackbar({
        message: 'Please approve allowance for staking amount',
        variant: 'info',
        persist: false,
      });

      await requestAllowance(
        BigInt(ethers.parseUnits(virtualBalance.toString(), 0))
      );
    }
  };

  const submitForm = async () => {
    try {
      setErrorMessage('');
      if (!convertAmount || Number(convertAmount) <= 0) return;
      setIsLoading(true);

      await checkNetwork();

      await checkAllowance(
        BigInt(ethers.parseUnits(convertAmount.toString(), 'ether'))
      );

      await convert();
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage = renderContractError(error);
      setErrorMessage(errorMessage);
      throw new Error(error);
    }
  };

  const retrieveMutiple = debounce((value: number) => {
    retrieveMultiplier(BigInt(monthsToSeconds(value)) as BigInt);
  }, 0);

  const handleTimeLockDuration = debounce((value: number) => {
    setTimelockDuration(value);
  }, 0);

  useEffect(() => {
    getTimelock();
  }, []);

  useEffect(() => {
    if (virtualBalance > 0) {
      const amnt = Number(ethers.formatUnits(virtualBalance));

      setConvertAmount(roundDown(amnt, 6).toString());
    }
  }, [virtualBalance]);

  useEffect(() => {
    let virtualBalanceNum = 0;

    if (virtualBalance) {
      virtualBalanceNum = Number(ethers.formatEther(virtualBalance));
    }

    if (convertAmount) {
      const convertAmountNum = Number(convertAmount);

      setCanConvert(
        roundDown(convertAmountNum, 2) > 0 &&
          convertAmountNum <= virtualBalanceNum
      );
    } else {
      setCanConvert(false);
    }
  }, [virtualBalance, convertAmount]);

  const secondsToMonths = (seconds: BigInt) => {
    const secondsNumber = Number(seconds);

    // Calculate the number of days
    const days = secondsNumber / (24 * 60 * 60);

    // Calculate the number of years
    const years = days / 365.25;

    // Calculate the number of months
    const months = years * 12;

    return months;
  };

  const monthsToSeconds = (months: number) => {
    // Calculate the number of years
    const years = months / 12;

    // Calculate the number of days
    const days = years * 365.25;

    // Calculate the total seconds
    const seconds = Math.floor(days * 24 * 60 * 60);

    return seconds;
  };

  return (
    <Stack p={3}>
      <Stack flex={1} justifyContent="space-between">
        <Box maxWidth={'100%'}>
          <Typography sx={{ color: '#404040', fontSize: '14px' }} mb={1}>
            To receive staking rewards, token holders are required to convert
            Virtual tokens to xVirtual. Staking rewards are distributed based on
            the amount of xVirtual staked. The conversion ratio from VIRTUAL to
            xVIRTUAL depends on the lock period; the longer the VIRTUAL tokens
            are locked, the more Virtual staking rewards will be awarded. Note
            that xVirtual tokens are non-transferable.
          </Typography>
          <Stack
            display={'flex'}
            justifyContent={'space-between'}
            flexDirection={'row'}
            alignItems={'center'}
            mb={1}
          >
            <Typography color={'#404040'}>Timelock</Typography>
            <Typography color={'#404040'}>
              {timelockDuration} {timelockDuration <= 1 ? 'month' : 'months'}
            </Typography>
          </Stack>
          <div
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
          >
            <Slider
              aria-label="timelock"
              max={Number(maxTimelock.toFixed(0))}
              min={Number(minTimelock.toFixed(0))}
              size="medium"
              onChange={(_, value) => setTimelockDuration(value as number)}
              onChangeCommitted={(_, value) => {
                setTimelockDuration(value as number);
                retrieveMutiple(value as number);
              }}
              defaultValue={timelockDuration}
              value={timelockDuration}
              color="primary"
            />
          </div>
        </Box>
        <Box
          maxWidth={'100%'}
          my={1}
          sx={{
            width: 'auto',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            marginBottom=".5rem"
          >
            <Typography sx={{ color: '#404040' }}>Amount to Convert</Typography>
            <VirtualBalanceChip value={virtualBalance} isVirtual={true} />
          </Box>
          <TokenInput
            maxAmount={Number(ethers.formatUnits(virtualBalance))}
            value={convertAmount}
            onChange={setConvertAmount}
          />
        </Box>
        <Box
          maxWidth={'100%'}
          my={1}
          sx={{
            width: 'auto',
          }}
        >
          <Typography sx={{ color: '#404040' }}>
            Conversion Information
          </Typography>

          <Box bgcolor={'#F5F5F5'} borderRadius={'8px'} p={1} my={1}>
            <Stack
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              flexDirection={'row'}
            >
              <Typography sx={{ color: '#404040' }}>
                $VIRTUAL to convert
              </Typography>
              <Typography sx={{ color: '#404040' }}>
                {convertAmount
                  ? (Math.floor(Number(convertAmount) * 100) / 100).toFixed(2)
                  : '0'}{' '}
                $VIRTUAL
              </Typography>
            </Stack>
            <Stack
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              flexDirection={'row'}
            >
              <Typography sx={{ color: '#404040' }}>Timelock</Typography>
              <Typography sx={{ color: '#404040' }}>
                {timelockDuration} {timelockDuration <= 1 ? 'month' : 'months'}
              </Typography>
            </Stack>
            <Stack
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              flexDirection={'row'}
            >
              <Typography sx={{ color: '#404040' }}>Multiplier</Typography>
              <Typography sx={{ color: '#404040' }}>
                {Number(ethers.formatEther(mutipleAmount)).toFixed(2)}x
              </Typography>
            </Stack>

            <Stack
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              flexDirection={'row'}
            >
              <Typography sx={{ color: '#404040' }}>
                $xVIRTUAL received
              </Typography>
              <Typography sx={{ color: '#404040' }}>
                {convertAmount
                  ? (
                      Number(convertAmount) *
                      Number(ethers.formatEther(mutipleAmount))
                    ).toFixed(2)
                  : 0}{' '}
                $xVIRTUAL
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Box height={'48px'}></Box>
        <LoadingButton
          disabled={
            !canConvert ||
            Number(convertAmount) >
              Number(ethers.formatUnits(virtualBalance as BigNumberish)) ||
            timelockDuration < 0
          }
          loading={isLoading || isTxConfirming}
          sx={virtualDetailModalStyle.btnStake}
          size="large"
          variant="contained"
          onClick={submitForm}
          fullWidth
        >
          Convert
        </LoadingButton>
        {errorMessage && errorMessage.length > 0 && (
          <Typography variant="body2" color="error" mt={1}>
            {errorMessage}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default ConvertVirtualForm;
